import "./styles.css";
import type { Grinder, GrindersByBrand } from "@/types/grinder";
import {
  loadGrinders,
  groupByBrand,
  getBrands,
  getModelsByBrand,
  findGrinder,
  stripHtml,
  positionToMicron,
} from "@/lib/data";
import { generateGrinderChart } from "@/lib/chart";
import { BREWING_METHODS } from "@/data/brewing-methods";

/**
 * Application state
 */
interface AppState {
  grindersByBrand: GrindersByBrand;
  selectedGrinder1: Grinder | null;
  selectedGrinder2: Grinder | null;
  comparisonLines: {
    line1: SVGLineElement | null;
    line2: SVGLineElement | null;
    label1: HTMLDivElement | null;
    label2: HTMLDivElement | null;
  };
  isRestoringFromUrl: boolean;
}

const state: AppState = {
  grindersByBrand: {},
  selectedGrinder1: null,
  selectedGrinder2: null,
  comparisonLines: {
    line1: null,
    line2: null,
    label1: null,
    label2: null,
  },
  isRestoringFromUrl: false,
};

/**
 * DOM Elements
 */
const elements = {
  brand1: document.getElementById("brand1") as HTMLSelectElement,
  model1: document.getElementById("model1") as HTMLSelectElement,
  brand2: document.getElementById("brand2") as HTMLSelectElement,
  model2: document.getElementById("model2") as HTMLSelectElement,
  chart1Container: document.getElementById(
    "chart1-container"
  ) as HTMLDivElement,
  chart2Container: document.getElementById(
    "chart2-container"
  ) as HTMLDivElement,
};

/**
 * URL state management
 */
interface UrlState {
  brand1?: string;
  model1?: string;
  brand2?: string;
  model2?: string;
}

function getUrlState(): UrlState {
  const params = new URLSearchParams(window.location.search);
  return {
    brand1: params.get("brand1") || undefined,
    model1: params.get("model1") || undefined,
    brand2: params.get("brand2") || undefined,
    model2: params.get("model2") || undefined,
  };
}

function updateUrl(): void {
  // Don't update URL if we're in the middle of restoring from URL
  if (state.isRestoringFromUrl) {
    return;
  }

  const params = new URLSearchParams();

  if (elements.brand1.value) {
    params.set("brand1", elements.brand1.value);
  }
  if (elements.model1.value) {
    params.set("model1", elements.model1.value);
  }
  if (elements.brand2.value) {
    params.set("brand2", elements.brand2.value);
  }
  if (elements.model2.value) {
    params.set("model2", elements.model2.value);
  }

  const newUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;

  window.history.replaceState({}, "", newUrl);
}

function restoreFromUrl(): void {
  const urlState = getUrlState();

  // If no URL parameters, nothing to restore
  if (!urlState.brand1 && !urlState.brand2) {
    return;
  }

  state.isRestoringFromUrl = true;

  // Restore grinder 1
  if (urlState.brand1) {
    elements.brand1.value = urlState.brand1;
    populateModelDropdown(elements.model1, urlState.brand1);

    if (urlState.model1) {
      elements.model1.value = urlState.model1;
      const grinder = findGrinder(
        state.grindersByBrand,
        urlState.brand1,
        urlState.model1
      );
      if (grinder) {
        state.selectedGrinder1 = grinder;
        renderChart(grinder, 1);
      }
    }
  }

  // Restore grinder 2
  if (urlState.brand2) {
    elements.brand2.value = urlState.brand2;
    populateModelDropdown(elements.model2, urlState.brand2);

    if (urlState.model2) {
      elements.model2.value = urlState.model2;
      const grinder = findGrinder(
        state.grindersByBrand,
        urlState.brand2,
        urlState.model2
      );
      if (grinder) {
        state.selectedGrinder2 = grinder;
        renderChart(grinder, 2);
      }
    }
  }

  state.isRestoringFromUrl = false;
}

/**
 * Initialize the application
 */
async function init(): Promise<void> {
  try {
    console.log("Loading grinder data...");

    // Load and organize grinder data
    const data = await loadGrinders();
    state.grindersByBrand = groupByBrand(data.grinders);
    const brands = getBrands(state.grindersByBrand);

    console.log(
      `Loaded ${data.total_grinders} grinders from ${brands.length} brands`
    );

    // Populate brand dropdowns
    populateBrandDropdown(elements.brand1, brands);
    populateBrandDropdown(elements.brand2, brands);

    // Setup event listeners
    setupEventListeners();

    // Restore state from URL if present
    restoreFromUrl();

    console.log("Application initialized successfully");
  } catch (error) {
    console.error("Failed to initialize application:", error);
    showError("Failed to load grinder data. Please refresh the page.");
  }
}

/**
 * Populate a brand dropdown with options
 */
function populateBrandDropdown(
  select: HTMLSelectElement,
  brands: string[]
): void {
  // Keep the first option (placeholder)
  const placeholder = select.options[0];
  select.innerHTML = "";
  if (placeholder) {
    select.appendChild(placeholder);
  }

  // Add brand options
  brands.forEach((brand) => {
    const option = document.createElement("option");
    option.value = brand;
    option.textContent = brand;
    select.appendChild(option);
  });
}

/**
 * Populate a model dropdown with options for a given brand
 */
function populateModelDropdown(select: HTMLSelectElement, brand: string): void {
  // Clear existing options
  select.innerHTML = "";

  if (!brand) {
    select.disabled = true;
    const option = document.createElement("option");
    option.value = "";
    option.textContent = "Select a brand first...";
    select.appendChild(option);
    return;
  }

  // Enable dropdown
  select.disabled = false;

  // Add placeholder
  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = "Select a model...";
  select.appendChild(placeholder);

  // Add model options
  const models = getModelsByBrand(state.grindersByBrand, brand);
  models.forEach((grinder) => {
    const option = document.createElement("option");
    option.value = grinder.model;
    option.textContent = grinder.model;
    select.appendChild(option);
  });
}

/**
 * Setup event listeners
 */
function setupEventListeners(): void {
  // Brand 1 selection
  elements.brand1.addEventListener("change", () => {
    const brand = elements.brand1.value;
    populateModelDropdown(elements.model1, brand);

    if (brand) {
      // Auto-select first model
      const models = getModelsByBrand(state.grindersByBrand, brand);
      if (models.length > 0 && models[0]) {
        elements.model1.value = models[0].model;
        // Trigger change event to render chart
        elements.model1.dispatchEvent(new Event("change"));
      }
    } else {
      elements.model1.value = "";
      clearChart(1);
      updateUrl();
    }
  });

  // Model 1 selection
  elements.model1.addEventListener("change", () => {
    const brand = elements.brand1.value;
    const model = elements.model1.value;

    if (brand && model) {
      const grinder = findGrinder(state.grindersByBrand, brand, model);
      if (grinder) {
        state.selectedGrinder1 = grinder;
        renderChart(grinder, 1);
      }
    } else {
      clearChart(1);
    }
    updateUrl();
  });

  // Brand 2 selection
  elements.brand2.addEventListener("change", () => {
    const brand = elements.brand2.value;
    populateModelDropdown(elements.model2, brand);

    if (brand) {
      // Auto-select first model
      const models = getModelsByBrand(state.grindersByBrand, brand);
      if (models.length > 0 && models[0]) {
        elements.model2.value = models[0].model;
        // Trigger change event to render chart
        elements.model2.dispatchEvent(new Event("change"));
      }
    } else {
      elements.model2.value = "";
      clearChart(2);
      updateUrl();
    }
  });

  // Model 2 selection
  elements.model2.addEventListener("change", () => {
    const brand = elements.brand2.value;
    const model = elements.model2.value;

    if (brand && model) {
      const grinder = findGrinder(state.grindersByBrand, brand, model);
      if (grinder) {
        state.selectedGrinder2 = grinder;
        renderChart(grinder, 2);
      }
    } else {
      clearChart(2);
    }
    updateUrl();
  });
}

/**
 * Render a chart for a grinder
 */
function renderChart(grinder: Grinder, chartNumber: 1 | 2): void {
  const container =
    chartNumber === 1 ? elements.chart1Container : elements.chart2Container;

  // Clear existing content
  container.innerHTML = "";

  // Create chart wrapper div
  const chartDiv = document.createElement("div");
  chartDiv.className = "chart-container";
  chartDiv.style.position = "relative";

  // Add grinder name header
  const headerDiv = document.createElement("div");
  headerDiv.className = "chart-header";
  headerDiv.style.marginBottom = "0.5rem";
  const heading = document.createElement("h3");
  heading.textContent = `${grinder.brand} ${grinder.model}`;
  heading.style.fontSize = "1rem";
  heading.style.fontWeight = "600";
  heading.style.color = "var(--primary-color)";
  heading.style.margin = "0";
  headerDiv.appendChild(heading);
  chartDiv.appendChild(headerDiv);

  // Generate SVG chart
  try {
    const svg = generateGrinderChart(grinder, BREWING_METHODS);
    svg.style.display = "block";
    chartDiv.appendChild(svg);

    // Add grinder info
    if (grinder.units || grinder.notes) {
      const infoDiv = document.createElement("div");
      infoDiv.className = "grinder-info";

      if (grinder.units) {
        const unitsP = document.createElement("p");
        unitsP.innerHTML = `<strong>Units:</strong> ${grinder.units}`;
        infoDiv.appendChild(unitsP);
      }

      if (grinder.notes) {
        const notesP = document.createElement("p");
        notesP.innerHTML = `<strong>Notes:</strong> ${stripHtml(
          grinder.notes
        )}`;
        infoDiv.appendChild(notesP);
      }

      chartDiv.appendChild(infoDiv);
    }

    container.appendChild(chartDiv);

    // Setup interactive comparison line after chart is rendered
    setupComparisonLine(svg, chartNumber);
  } catch (error) {
    console.error(
      `Failed to render chart for ${grinder.brand} ${grinder.model}:`,
      error
    );
    showErrorInContainer(container, "Failed to render chart");
  }
}

/**
 * Setup interactive comparison line on an SVG chart
 */
function setupComparisonLine(svg: SVGSVGElement, chartNumber: 1 | 2): void {
  const chartKey = chartNumber === 1 ? "line1" : "line2";
  const labelKey = chartNumber === 1 ? "label1" : "label2";
  const otherChartKey = chartNumber === 1 ? "line2" : "line1";
  const otherLabelKey = chartNumber === 1 ? "label2" : "label1";

  // Create comparison line element
  const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
  line.setAttribute("class", "comparison-line");
  line.setAttribute("y1", "0");
  line.setAttribute("y2", "100%");
  line.setAttribute("stroke", "var(--accent, #e74c3c)");
  line.setAttribute("stroke-width", "2");
  line.setAttribute("stroke-dasharray", "5,5");
  line.setAttribute("opacity", "0");
  line.setAttribute("pointer-events", "none");
  svg.appendChild(line);

  // Create micron label element (absolute positioned div)
  const label = document.createElement("div");
  label.className = "micron-label";
  label.style.cssText = `
    position: absolute;
    background: var(--accent, #e74c3c);
    color: white;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 600;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 100;
    white-space: nowrap;
  `;
  svg.parentElement?.appendChild(label);

  // Store references
  state.comparisonLines[chartKey] = line;
  state.comparisonLines[labelKey] = label;

  // Mouse event handlers
  const handleMouseMove = (e: MouseEvent): void => {
    const svgRect = svg.getBoundingClientRect();
    const mouseXScreen = e.clientX - svgRect.left;

    // Convert screen coordinates to SVG viewBox coordinates
    // SVG has viewBox="0 0 960 350" but renders at actual width
    const viewBoxWidth = 960;
    const scaleX = viewBoxWidth / svgRect.width;
    const mouseXSvg = mouseXScreen * scaleX;

    // Chart configuration (must match chart.ts defaults)
    const marginLeft = 30;
    const marginRight = 30;
    const chartWidth = viewBoxWidth - marginLeft - marginRight;
    const minMicron = 0;
    const maxMicron = 1400;

    // Calculate relative position within chart area (in SVG coordinates)
    const relativeX = mouseXSvg - marginLeft;

    // Only show line if within chart bounds
    if (relativeX >= 0 && relativeX <= chartWidth) {
      // Calculate micron value
      const micron = positionToMicron(
        relativeX,
        minMicron,
        maxMicron,
        chartWidth
      );

      // Update current chart's line (use SVG viewBox coordinates)
      line.setAttribute("x1", mouseXSvg.toString());
      line.setAttribute("x2", mouseXSvg.toString());
      line.setAttribute("opacity", "0.7");

      // Update current chart's label (use absolute screen position)
      // Position at the top of the SVG, aligned with the vertical line
      label.textContent = `${Math.round(micron)}μm`;
      label.style.opacity = "1";
      // Calculate screen X position from SVG coordinates (same as the line)
      const lineScreenX = mouseXSvg / scaleX;
      label.style.left = `${lineScreenX}px`;
      label.style.top = `${0}px`;

      // Update other chart's line if it exists
      const otherLine = state.comparisonLines[otherChartKey];
      const otherLabel = state.comparisonLines[otherLabelKey];
      if (otherLine && otherLabel) {
        // Use the same SVG coordinates for the other chart
        otherLine.setAttribute("x1", mouseXSvg.toString());
        otherLine.setAttribute("x2", mouseXSvg.toString());
        otherLine.setAttribute("opacity", "0.7");

        // Get other SVG position for label placement
        const otherSvg = otherLine.ownerSVGElement;
        if (otherSvg) {
          const otherRect = otherSvg.getBoundingClientRect();
          otherLabel.textContent = `${Math.round(micron)}μm`;
          otherLabel.style.opacity = "1";
          // Calculate screen X position from SVG coordinates (same as the line)
          const otherScaleX = viewBoxWidth / otherRect.width;
          const otherLineScreenX = mouseXSvg / otherScaleX;
          otherLabel.style.left = `${otherLineScreenX}px`;
          otherLabel.style.top = `${0}px`;
        }
      }
    }
  };

  const handleMouseLeave = (): void => {
    // Hide current chart's line and label
    line.setAttribute("opacity", "0");
    label.style.opacity = "0";

    // Hide other chart's line and label
    const otherLine = state.comparisonLines[otherChartKey];
    const otherLabel = state.comparisonLines[otherLabelKey];
    if (otherLine) {
      otherLine.setAttribute("opacity", "0");
    }
    if (otherLabel) {
      otherLabel.style.opacity = "0";
    }
  };

  // Attach event listeners
  svg.addEventListener("mousemove", handleMouseMove);
  svg.addEventListener("mouseleave", handleMouseLeave);
}

/**
 * Clear a chart
 */
function clearChart(chartNumber: 1 | 2): void {
  const container =
    chartNumber === 1 ? elements.chart1Container : elements.chart2Container;
  container.innerHTML =
    '<div class="chart-placeholder">Select a grinder to view chart</div>';

  // Clear comparison line references
  const lineKey = chartNumber === 1 ? "line1" : "line2";
  const labelKey = chartNumber === 1 ? "label1" : "label2";
  state.comparisonLines[lineKey] = null;
  state.comparisonLines[labelKey] = null;

  if (chartNumber === 1) {
    state.selectedGrinder1 = null;
  } else {
    state.selectedGrinder2 = null;
  }
}

/**
 * Show an error message in a container
 */
function showErrorInContainer(container: HTMLElement, message: string): void {
  container.innerHTML = `<div class="chart-placeholder" style="color: var(--accent);">${message}</div>`;
}

/**
 * Show a global error message
 */
function showError(message: string): void {
  const errorDiv = document.createElement("div");
  errorDiv.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #f44336;
    color: white;
    padding: 1rem 2rem;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    z-index: 1000;
  `;
  errorDiv.textContent = message;
  document.body.appendChild(errorDiv);

  setTimeout(() => {
    errorDiv.remove();
  }, 5000);
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  console.log("Coffee Grinder Size Converter - Starting...");
  init();
});
